import { styled } from '@mui/material/styles'
import {
  Button as MuiButton,
  Grid as MuiGrid,
  TextField as MuiTextField,
  Alert,
  AlertTitle,
  Stack
} from '@mui/material'
import * as Yup from 'yup'
import { spacing } from '@mui/system'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useNavigate } from 'react-router-dom'
import userService from '../../services/userService'
import { NotificationType } from '../../enums/NotificationType'
import { deleteError, setError } from '../../redux/slices/errors'
import { useAuth } from '../../hooks'
import { createErrorOrSuccessNotification } from '../../redux/slices/notifications'
import { EmailErrors } from '../../enums/EmailErrors'
import { createUserNotification } from '../../utils/createUserNotification'
import { SendEmailFromInstaller } from '../../types'
import { LoadingButton } from '@mui/lab'
import { isRequired, mustBeString } from '../../utils/validation'

/**
 * The styled Textfield
 */
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)

/**
 * The styled Button
 */
const Button = styled(MuiButton)(spacing)

/**
 * The styled Grid
 */
const Grid = styled(MuiGrid)(spacing)

/**
 * The properties
 */
interface EmailFormProps {
  projectId: number
  emailSent(): void
}

export default function EmailForm({ projectId, emailSent }: EmailFormProps) {
  /**
   * The dispatch function
   */
  const dispatch = useDispatch()

  /**
   * The navigate function
   */
  const navigate = useNavigate()

  /**
   * The translate function
   */
  const [t] = useTranslation('common')

  /**
   * The validation schema
   */
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .typeError(mustBeString(t('email')))
      .required(isRequired(t('email')))
  })

  /**
   * The initial values
   */
  const initialValues: SendEmailFromInstaller = {
    email: '',
    projectId: projectId
  }

  /**
   * Current user
   */
  const { currentUser } = useAuth()

  /**
   * Handle submit
   * @param values
   */
  const handleSubmit = async (
    values: SendEmailFromInstaller,
    {
      resetForm,
      setSubmitting
    }: {
      resetForm: () => void
      setSubmitting: (value: boolean) => void
    }
  ) => {
    try {
      await userService.sendSendEmailFromInstaller(values)

      dispatch(createErrorOrSuccessNotification(NotificationType.SUCCESS, t('emailSentSuccess')))
      emailSent()
      resetForm()
    } catch (error: unknown) {
      dispatch(
        setError({
          type: EmailErrors.SEND,
          error: error
        })
      )

      //Create message
      const errorMessage = createUserNotification({
        user: currentUser,
        type: EmailErrors.SEND,
        error: error
      })

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )

      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(EmailErrors.SEND))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                <AlertTitle>{t('Manufacturing.giveForemanEmail')}</AlertTitle>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                name="email"
                label={t('Manufacturing.email')}
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
              <Button
                onClick={() => {
                  navigate('/install/device')
                }}
                variant="text"
                color="inherit"
              >
                {t('Manufacturing.cancel')}
              </Button>

              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isSubmitting}
              >
                {t('Manufacturing.send')}
              </LoadingButton>
            </Stack>
          </Grid>
        </form>
      )}
    </Formik>
  )
}
